// ============================================================
// pages/admin/AdminReviews.jsx — Review Moderation (Firebase)
// ============================================================

import React, { useState } from "react";
import { useReviews } from "../../hooks/useFirestore";
import { ReviewService } from "../../services/firebaseServices";

export default function AdminReviews({ showToast }) {
  const { reviews, loading } = useReviews();
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState("");

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) return;
    try {
      await ReviewService.addAdminReply(reviewId, replyText);
      showToast("Reply posted!", "success");
      setReplyingTo(null);
      setReplyText("");
    } catch (err) {
      showToast("Failed to post reply", "error");
    }
  };

  const handleToggleApproval = async (review) => {
    try {
      await ReviewService.toggleApproval(review.id, !review.isApproved);
      showToast(review.isApproved ? "Review hidden" : "Review approved", "success");
    } catch (err) {
      showToast("Failed to update review", "error");
    }
  };

  const formatDate = (ts) => {
    if (!ts) return "—";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  // Stats
  const avgRating = reviews.length
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : "—";
  const fiveStars = reviews.filter((r) => r.rating === 5).length;
  const flagged = reviews.filter((r) => r.isFlagged).length;

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Customer Reviews</h2>
          <p>Monitor feedback, respond, and moderate reviews</p>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: "2rem" }}>
        <div className="stat-card">
          <div className="stat-label">Total Reviews</div>
          <div className="stat-value">{reviews.length}</div>
        </div>
        <div className="stat-card sage">
          <div className="stat-label">Average Rating</div>
          <div className="stat-value">⭐ {avgRating}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">5-Star Reviews</div>
          <div className="stat-value">{fiveStars}</div>
        </div>
        <div className="stat-card berry">
          <div className="stat-label">Flagged</div>
          <div className="stat-value">{flagged}</div>
        </div>
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading reviews...</p></div>
      ) : reviews.length === 0 ? (
        <div className="empty-state">
          <h3>No reviews yet</h3>
          <p>Reviews will appear here once customers submit feedback.</p>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: "1rem" }}>
          {reviews.map((r) => (
            <div key={r.id} className="review-card" style={{
              opacity: r.isApproved === false ? 0.5 : 1,
              border: r.isFlagged ? "2px solid #e74c3c" : "1px solid rgba(0,0,0,0.04)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "0.5rem" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <span style={{ fontWeight: 600 }}>{r.customerName}</span>
                    {r.isFlagged && <span className="inline-tag" style={{ background: "#F8D7DA", color: "#721C24" }}>Flagged</span>}
                    {r.isApproved === false && <span className="inline-tag" style={{ background: "#FFF3CD", color: "#856404" }}>Hidden</span>}
                  </div>
                  <div style={{ fontSize: "0.78rem", color: "var(--cocoa)" }}>{r.productName}</div>
                </div>
                <div className="review-stars">
                  {Array.from({ length: r.rating }).map((_, i) => (
                    <svg key={i} viewBox="0 0 24 24" style={{ width: 16, height: 16, fill: "#f5a623", stroke: "#f5a623" }}>
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
              </div>

              <p style={{ fontSize: "0.88rem", lineHeight: 1.6, marginBottom: "0.5rem" }}>"{r.comment}"</p>

              <div style={{ fontSize: "0.78rem", color: "var(--cocoa)", marginBottom: "0.75rem" }}>
                {formatDate(r.createdAt)}
              </div>

              {/* Admin Reply */}
              {r.adminReply && (
                <div style={{
                  background: "rgba(196,136,58,0.06)", borderRadius: 8,
                  padding: "0.75rem", marginBottom: "0.75rem", borderLeft: "3px solid var(--chai)",
                }}>
                  <div style={{ fontSize: "0.78rem", fontWeight: 600, color: "var(--chai)", marginBottom: 4 }}>
                    Chaitimecrunch replied
                  </div>
                  <p style={{ fontSize: "0.85rem", lineHeight: 1.5 }}>{r.adminReply}</p>
                </div>
              )}

              {/* Reply form */}
              {replyingTo === r.id && (
                <div style={{ marginBottom: "0.75rem" }}>
                  <textarea className="form-textarea" rows="2" value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write your reply..." />
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                    <button className="btn btn-primary btn-sm" onClick={() => handleReply(r.id)}>
                      Post Reply
                    </button>
                    <button className="btn btn-outline btn-sm" onClick={() => { setReplyingTo(null); setReplyText(""); }}>
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: "flex", gap: "0.5rem" }}>
                {!r.adminReply && replyingTo !== r.id && (
                  <button className="btn btn-outline btn-sm"
                    onClick={() => { setReplyingTo(r.id); setReplyText(""); }}>
                    Reply
                  </button>
                )}
                <button className="btn btn-sm"
                  style={{ background: r.isApproved ? "#FFF3CD" : "#D4EDDA", color: r.isApproved ? "#856404" : "#155724" }}
                  onClick={() => handleToggleApproval(r)}>
                  {r.isApproved ? "Hide" : "Approve"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
